import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { MITPService } from "./mitp.service";
import { AutocompleteDrug, Drug, DrugDetails } from "../entities/drug";
import { EMPTY, first, Observable, of, switchMap } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { DrugDetailsComponent } from "../modules/pharmacy/drug-details/drug-details.component";
import { NotificationService } from "./notification.service";
import { DrugInstitute } from "../entities/pharmacy/drug-institute";
import { catchError, map } from "rxjs/operators";
import { TranslateService } from "@tolgee/ngx";

@Injectable({
	providedIn: "root",
})
export class DrugsService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);
	private dialog = inject(MatDialog);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);

	search(keyword: string, booklet: boolean = true): Observable<AutocompleteDrug[]> {
		return this.httpClient.get<AutocompleteDrug[]>(
			this.mitp.Drugs() + `/search?keyword=${keyword}&booklet=${booklet}`
		);
	}

	getDrugByUcd(ucd: string): Observable<Drug> {
		return this.httpClient.get<Drug>(this.mitp.Drugs() + `/ucd/${ucd}`);
	}

	getDrugInstituteByUcd(ucd: string): Observable<DrugInstitute> {
		return this.httpClient.get<DrugInstitute>(this.mitp.Drugs() + `/ucd/${ucd}/institutes`);
	}

	getContraindications(vmpId: string): Observable<{ contraindications: string[] }> {
		return this.httpClient.get<{ contraindications: string[] }>(
			this.mitp.Drugs() + `/contraindications/vmp/${vmpId}`
		);
	}

	getDrugDetails(ucd: string): Observable<DrugDetails> {
		return this.httpClient.get<DrugDetails>(this.mitp.Drugs() + `/ucd/${ucd}/details`);
	}

	checkShortage(ucds: string[]): Observable<string[]> {
		return this.httpClient.post<string[]>(this.mitp.Drugs() + `/shortage/ucds`, { ucds: ucds });
	}

	showDrug(ucd: string | DrugInstitute): void {
		if (typeof ucd === "string") {
			this.getDrugInstituteByUcd(ucd)
				.pipe(
					first(),
					switchMap((drugInstitute) => {
						if (drugInstitute) {
							return of(drugInstitute);
						} else {
							return this.getDrugByUcd(ucd).pipe(
								first(),
								map(
									(drug) =>
										({
											alternatives: [],
											drug: drug,
											id: -1,
											institute: undefined,
											lastUpdated: undefined,
											reference: "",
											risk: false,
											stock: null,
										}) as DrugInstitute
								)
							);
						}
					}),
					catchError(() => {
						this.toastr.error(this.translate.instant("drugs.drug.search.error"));
						return EMPTY;
					})
				)
				.subscribe((drugInstitute) => this.openDrugDetails(drugInstitute));
		} else {
			this.openDrugDetails(ucd);
		}
	}

	openDrugDetails(drugInstitute: DrugInstitute): void {
		this.dialog.open(DrugDetailsComponent, {
			data: drugInstitute,
			width: "600px",
			autoFocus: false,
		});
	}
}
